import { isAxiosError } from 'axios';
import type { ReactNode } from 'react';
import { ErrorBoundary, type FallbackProps } from 'react-error-boundary';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router';
import { routes } from 'routes/routes';
import { Button } from '../button';
import { Result } from '../result';

type AxiosErrorBoundaryProperties = {
  actions?: boolean;
  children: ReactNode;
  fallbackRender?: (props: FallbackProps) => ReactNode;
  handleUnauthorized?: boolean;
};

type AxiosErrorFallbackProperties = {
  error: unknown;
  onRetry: () => void;
} & Omit<AxiosErrorBoundaryProperties, 'children' | 'fallbackRender'>;

const AxiosErrorFallback = ({
  actions = true,
  error,
  handleUnauthorized = false,
  onRetry,
}: AxiosErrorFallbackProperties) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  if (isAxiosError(error)) {
    if (!error.response) {
      return (
        <Result
          extra={
            actions ? (
              <Button
                onClick={() => {
                  onRetry();
                }}
                size="large"
                type="primary"
              >
                Retry
              </Button>
            ) : null
          }
          subTitle={t('Please check your internet connection.')}
        />
      );
    }

    if (error.response.status === 401 && !handleUnauthorized) {
      throw error;
    }

    const resultDataMap: Record<number, { action: ReactNode; status: 403 | 404; subTitle: string } | undefined> = {
      401: {
        action: (
          <Button
            onClick={async () => {
              await navigate(routes.login.index);
            }}
            size="large"
            type="primary"
          >
            Home
          </Button>
        ),
        status: 403,
        subTitle: t('Sorry, you are not authorized to access this resource.'),
      },
      403: {
        action: (
          <Button
            onClick={async () => {
              await navigate('/');
            }}
            size="large"
            type="primary"
          >
            Home
          </Button>
        ),
        status: 403,
        subTitle: t('You are not permitted to access this data.'),
      },
      404: {
        action: (
          <Button
            onClick={async () => {
              await navigate('/');
            }}
            size="large"
            type="primary"
          >
            Home
          </Button>
        ),
        status: 404,
        subTitle: t('Sorry, resource does not exist.'),
      },
    };

    const resultData = resultDataMap[error.response.status];
    if (resultData) {
      return (
        <Result extra={actions ? resultData.action : null} status={resultData.status} subTitle={resultData.subTitle} />
      );
    }
  }

  return (
    <Result
      extra={
        actions ? (
          <Button
            onClick={() => {
              onRetry();
            }}
            size="large"
            type="primary"
          >
            Retry
          </Button>
        ) : null
      }
      status="500"
      subTitle={t('Sorry, something went wrong.')}
    />
  );
};

const AxiosErrorBoundary = ({ children, fallbackRender, ...rest }: AxiosErrorBoundaryProperties) => (
  <ErrorBoundary
    fallbackRender={
      fallbackRender ??
      (({ error, resetErrorBoundary }) => <AxiosErrorFallback error={error} onRetry={resetErrorBoundary} {...rest} />)
    }
  >
    {children}
  </ErrorBoundary>
);

export default AxiosErrorBoundary;
